import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Pencil, Archive } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { StatTile } from "@/ui/StatTile";
import { Card, CardBody } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { Tabs, type TabDef } from "@/ui/Tabs";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { EmptyState, LoadingState } from "@/ui/feedback/states";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD, formatMoney, formatUSD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import { payments, adSpends, tasks } from "@/mocks/seed";
import type { Payment, AdSpend } from "@/types";
import { useClient, useClientMutations } from "./clients.hooks";
import { ClientFormModal, type ClientFormValues } from "./components/ClientFormModal";
import { ClientSocialLinks } from "./components/ClientSocialLinks";

function displayDate(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

export function ClientDetailsPage() {
  const { t } = useTranslation();
  const { CLIENT_STATUS, CLIENT_TYPE, CONTRACT_MODEL, SERVICE_TYPE, PAYMENT_STATUS, PAYMENT_TYPE, PLATFORM } = useLabels();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { client, isLoading } = useClient(clientId);
  const mutations = useClientMutations();
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const tabs: TabDef[] = useMemo(
    () => [
      { value: "overview", label: t("clients.tabs.overview") },
      { value: "wallet", label: t("clients.tabs.wallet") },
      { value: "payments", label: t("clients.tabs.payments") },
      { value: "spend", label: t("clients.tabs.spend") },
      { value: "tasks", label: t("clients.tabs.tasks") },
    ],
    [t],
  );

  const clientPayments = useMemo(() => payments.filter((p) => p.clientId === clientId), [clientId]);
  const clientSpend = useMemo(() => adSpends.filter((a) => a.clientId === clientId), [clientId]);
  const clientTasks = useMemo(() => tasks.filter((task) => task.clientName === client?.name), [client]);

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState rows={6} />
      </PageContainer>
    );
  }

  if (!client) {
    return (
      <PageContainer>
        <EmptyState
          title={t("clients.details.notFoundTitle")}
          message={t("clients.details.notFoundMessage")}
          action={
            <Button size="sm" onClick={() => navigate("/clients")}>
              {t("clients.details.back")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const onSave = async (values: ClientFormValues) => {
    try {
      await mutations.update(client.id, values);
      toast.success(t("clients.saveUpdated", { name: values.name }));
      setEditOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("clients.saveFailed")));
    }
  };

  const paymentCols: Column<Payment>[] = [
    { key: "type", header: t("clients.details.paymentType"), cell: (payment) => PAYMENT_TYPE[payment.type].label },
    { key: "amount", header: t("clients.details.amount"), align: "right", cell: (payment) => <span className="mono">{formatDZD(payment.amount)}</span> },
    {
      key: "status",
      header: t("clients.details.status"),
      cell: (payment) => (
        <Badge tone={PAYMENT_STATUS[payment.status].tone} dot>
          {PAYMENT_STATUS[payment.status].label}
        </Badge>
      ),
    },
    { key: "date", header: t("clients.details.date"), cell: (payment) => payment.date },
  ];

  const spendCols: Column<AdSpend>[] = [
    { key: "date", header: t("clients.details.date"), cell: (spend) => spend.date },
    { key: "platform", header: t("clients.details.platform"), cell: (spend) => <Badge tone={PLATFORM[spend.platform].tone}>{PLATFORM[spend.platform].label}</Badge> },
    { key: "usd", header: "USD", align: "right", cell: (spend) => <span className="mono">{formatUSD(spend.amountUsd)}</span> },
    { key: "dzd", header: t("clients.details.dzdEquivalent"), align: "right", cell: (spend) => <span className="mono text-text2">{formatDZD(spend.amountDzd)}</span> },
  ];

  const infoRows = [
    [t("clients.details.name"), client.name],
    [t("clients.details.email"), client.email ?? "-"],
    [t("clients.details.phone"), client.phone ?? "-"],
    [t("clients.details.type"), CLIENT_TYPE[client.type].label],
    [t("clients.details.niche"), client.niche ?? "-"],
    [t("clients.details.service"), SERVICE_TYPE[client.service].label],
    [t("clients.details.contractModel"), CONTRACT_MODEL[client.contractModel].label],
    [
      t("clients.details.monthlyFee"),
      client.monthlyFee ? `${formatMoney(client.monthlyFee, client.currency ?? "DZD")}/${t("clients.details.month")}` : "-",
    ],
    [t("clients.details.startDate"), displayDate(client.startDate, client.since)],
  ];

  return (
    <PageContainer>
      <Button variant="ghost" size="sm" className="mb-3" onClick={() => navigate("/clients")}>
        <ArrowLeft className="h-3.5 w-3.5" /> {t("clients.details.back")}
      </Button>

      <Card className="mb-5">
        <CardBody className="flex items-start gap-4">
          <Avatar name={client.name} size={56} className="rounded-lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-display text-[25px] font-bold text-text">{client.name}</h1>
              <Badge tone={CLIENT_STATUS[client.status].tone} dot>
                {CLIENT_STATUS[client.status].label}
              </Badge>
              <Badge tone={CONTRACT_MODEL[client.contractModel].tone}>{CONTRACT_MODEL[client.contractModel].label}</Badge>
            </div>
            <p className="text-xs text-text2 mt-1">
              {SERVICE_TYPE[client.service].label} / {t("clients.since")} {client.since}
            </p>
            <ClientSocialLinks client={client} className="mt-3" showEmpty />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3 w-3" /> {t("common.edit")}
            </Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmArchive(true)}>
              <Archive className="h-3 w-3" /> {t("common.archive")}
            </Button>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatTile label={t("clients.details.receivedRevenue")} value={<span className="text-success">{formatDZD(client.revenue)}</span>} tone="success" />
        <StatTile label={t("clients.details.pending")} value={<span className="text-text3">0 DZD</span>} />
        <StatTile label={t("clients.details.adSpend")} value={formatUSD(client.adSpendUsd)} tone="info" />
        <StatTile label={t("clients.details.walletBalance")} value={<span className={client.walletUsd < 0 ? "text-danger" : ""}>{formatUSD(client.walletUsd)}</span>} tone={client.walletUsd < 0 ? "danger" : "violet"} />
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <Card>
            <CardBody>
              <div className="text-[13px] font-bold text-text mb-3.5">{t("clients.details.information")}</div>
              {infoRows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 py-2.5 border-b border-border last:border-0">
                  <span className="text-xs text-text2">{label}</span>
                  <span className="text-[12.5px] font-semibold text-text text-right">{value}</span>
                </div>
              ))}
              {client.notes && (
                <div className="pt-3 mt-1 border-t border-border">
                  <div className="text-xs text-text2 mb-1">{t("clients.details.notes")}</div>
                  <p className="text-[12.5px] leading-5 text-text">{client.notes}</p>
                </div>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <div className="text-[13px] font-bold text-text mb-3.5">{t("clients.details.socialProfiles")}</div>
              <ClientSocialLinks client={client} showEmpty />
              <div className="text-[13px] font-bold text-text mt-5 mb-3.5">{t("clients.details.activity")}</div>
              <p className="text-xs text-text2">
                {t("clients.details.activitySummary", {
                  payments: clientPayments.length,
                  spend: clientSpend.length,
                  tasks: clientTasks.length,
                })}
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      {tab === "wallet" && (
        <Card>
          <CardBody>
            <div className="text-[13px] font-bold text-text mb-3.5">{t("clients.details.currentBalance")}</div>
            {[
              [t("clients.details.availableUsd"), formatUSD(client.walletUsd)],
              [t("clients.details.spentUsd"), formatUSD(client.adSpendUsd)],
              [t("clients.details.currentRate"), "218.5 DZD/$"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <span className="text-xs text-text2">{label}</span>
                <span className="mono text-[12.5px] font-semibold text-text">{value}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {tab === "payments" && (
        <Card>
          <DataTable columns={paymentCols} rows={clientPayments} rowKey={(payment) => payment.id} empty={{ title: t("clients.details.noPayments") }} />
        </Card>
      )}

      {tab === "spend" && (
        <Card>
          <DataTable columns={spendCols} rows={clientSpend} rowKey={(spend) => spend.id} empty={{ title: t("clients.details.noSpend") }} />
        </Card>
      )}

      {tab === "tasks" && (
        <Card>
          <CardBody>
            {clientTasks.length === 0 ? (
              <EmptyState title={t("clients.details.noTasks")} message={t("clients.details.noTasksMessage")} />
            ) : (
              <div className="space-y-2">
                {clientTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-[12.5px] font-semibold text-text">{task.title}</span>
                    <span className="text-xs text-text3">{task.dueDate}</span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <ClientFormModal open={editOpen} onClose={() => setEditOpen(false)} onSave={onSave} initial={client} />
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        title={t("clients.archiveTitle")}
        message={t("clients.archiveMessage", { name: client.name })}
        confirmLabel={t("common.archive")}
        onConfirm={async () => {
          try {
            await mutations.archive(client.id);
            toast.success(t("clients.archiveSuccess", { name: client.name }));
            navigate("/clients");
          } catch (err) {
            toast.error(getErrorMessage(err, t("clients.archiveFailed")));
          }
        }}
      />
    </PageContainer>
  );
}
